
export type VoucherTypeCode = 'JV' | 'CP' | 'CR' | 'BP' | 'BR';

export type Side = 'Dr' | 'Cr';

export interface VoucherTypeConfig {
  code: VoucherTypeCode;
  label: string;
  description: string;
  shape: 'JOURNAL' | 'CONTRA';
  contra?: {
    kind: 'CASH' | 'BANK';
    side: Side;
    label: string;
    pickable?: boolean;
  };
  analysisSide: Side;
  amountLabel: string;
  narrationPlaceholder: string;
  approvalThreshold: string;
  lineHelp: string;
}

export const VOUCHER_TYPES: Record<VoucherTypeCode, VoucherTypeConfig> = {
  JV: {
    code: 'JV',
    label: 'Journal Voucher',
    description: 'Enter debits and credits by hand; the two sides must add up to the same total.',
    shape: 'JOURNAL',
    analysisSide: 'Dr',
    amountLabel: 'Amount',
    narrationPlaceholder: 'Freight accrual — Lot IMP-26-118',
    approvalThreshold: '1000000.0000',
    lineHelp:
      'A control account takes no hand-written JV line — that would silently break the sub-ledger reconciliation, and trg_voucher_lines_derive rejects it for JV specifically. Where one has been broken out into 5th-level sub-accounts, choose the head and then the customer, supplier or salesperson it posts to: the line lands on the sub-account, and the sub-ledger still reconciles.',
  },
  CP: {
    code: 'CP',
    label: 'Cash Payment',
    description: 'Record money paid out of the cash till.',
    shape: 'CONTRA',
    contra: { kind: 'CASH', side: 'Cr', label: 'Paid from' },
    analysisSide: 'Dr',
    amountLabel: 'Payment',
    narrationPlaceholder: 'Warehouse labour — Karachi, week 30',
    approvalThreshold: '250000.0000',
    lineHelp:
      'Debit what the cash bought: an expense, an advance, or Accounts Payable when it clears a supplier bill. Set the party in the header for the last of those.',
  },
  CR: {
    code: 'CR',
    label: 'Cash Receipt',
    description: 'Record money received into the cash till.',
    shape: 'CONTRA',
    contra: { kind: 'CASH', side: 'Dr', label: 'Received into' },
    analysisSide: 'Cr',
    amountLabel: 'Receipt',
    narrationPlaceholder: 'Cash received against INV-26-004460',
    approvalThreshold: '1000000.0000',
    lineHelp:
      'A receipt credits Accounts Receivable directly — the control-account restriction applies to JV only, because this is the document the sub-ledger expects.',
  },
  BP: {
    code: 'BP',
    label: 'Bank Payment',
    description: 'Record money paid out of a bank account.',
    shape: 'CONTRA',
    contra: {
      kind: 'BANK',
      side: 'Cr',
      label: 'Paid from',
      pickable: true,
    },
    analysisSide: 'Dr',
    amountLabel: 'Payment',
    narrationPlaceholder: 'Punjab Oil Mills — PUR-26-000318 — cheque 004417',
    approvalThreshold: '500000.0000',
    lineHelp:
      'Debit what the payment settles: Accounts Payable with the party set to clear a supplier bill, or an expense directly. Put the cheque or transfer reference in the narration — the ledger no longer keeps a column for it.',
  },
  BR: {
    code: 'BR',
    label: 'Bank Receipt',
    description: 'Record money received into a bank account.',
    shape: 'CONTRA',
    contra: {
      kind: 'BANK',
      side: 'Dr',
      label: 'Received into',
      pickable: true,
    },
    analysisSide: 'Cr',
    amountLabel: 'Receipt',
    narrationPlaceholder: 'Received — Meezan 0192 — TT ref 88410-KWN',
    approvalThreshold: '1000000.0000',
    lineHelp:
      'A receipt credits Accounts Receivable directly — the control-account restriction applies to JV only. Record the transfer or cheque reference in the narration.',
  },
};

export const VOUCHER_TYPE_CODES = Object.keys(VOUCHER_TYPES) as VoucherTypeCode[];

export function isVoucherTypeCode(key: string): key is VoucherTypeCode {
  return key.toUpperCase() in VOUCHER_TYPES;
}
